/** @module Auth0USerSessionHelper */
/* eslint-disable import/extensions */
import Auth0LocalStorageHelper from './auth0-local-storage-helper.mjs';
import Auth0CookieHelper from './auth0-cookie-helper.mjs';
import Auth0Constants from './auth0-constants.mjs';

class Auth0UserSessionHelper {
  // ID TOKEN

  static get userIdentityToken() {
    return this._userIdentityTokenValueObject?.id_token;
  }

  static get userIdentityTokenDecoded() {
    return this._userIdentityTokenValueObject?.decodedToken;
  }

  static get userIdentityTokenIssuedAt() {
    return this.userClaims?.iat;
  }

  static get userIdentityTokenExpiry() {
    return this.userClaims?.exp;
  }

  static get isLoggedIn() {
    return !!this.userIdentityToken;
  }

  /**
   * Check if current user is linked to a ShipStation account.
   * Uses multiple detection methods for cross-tenant compatibility:
   * 1. Primary: Check if sub starts with 'auth0|' (universal - works for all tenants)
   *    When SSO user links to SS account, they authenticate AS the primary (auth0|) user
   * 2. Legacy: Check for seller_id claim (ShipStation-specific, kept for backwards compat)
   */
  static get isLinkedToSsAccount() {
    return this.isAuthenticatedAsPrimaryUser || !!this.userSellerId;
  }

  /**
   * Check if current user authenticated as a primary (database) user.
   * When SSO users link their account, they authenticate AS the primary user,
   * so their sub claim changes from 'google-oauth2|...' to 'auth0|...'.
   * This is a universal indicator that works across all tenant classes.
   */
  static get isAuthenticatedAsPrimaryUser() {
    const sub = this.auth0UserId;
    return sub?.startsWith(Auth0Constants.AUTH0_ID_PREFIX) ?? false;
  }

  /**
   * Check if current user authenticated via an SSO IdP (not yet linked).
   * SSO users have sub claims like 'google-oauth2|...', 'microsoft|...', etc.
   */
  static get isAuthenticatedAsSsoUser() {
    const sub = this.auth0UserId;
    // If no sub, not authenticated at all
    if (!sub) return false;
    // If starts with auth0|, it's a primary (database) user, not pure SSO
    return !sub.startsWith(Auth0Constants.AUTH0_ID_PREFIX);
  }

  static get isNativeSsoSeller() {
    return !!this.nativeSsoSellerIdp;
  }

  static get nativeSsoSellerIdp() {
    const hasNativeCookie = Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_IS_NATIVE_SSO_SELLER);
    if (hasNativeCookie) return hasNativeCookie;
    const claim = this.getShipStationUserClaim('native_sso_seller');
    return claim && claim !== 'false';
  }

  static get userId() {
    return this.auth0UserId?.replace(Auth0Constants.AUTH0_ID_PREFIX, '');
  }

  static get auth0UserId() {
    return this.userClaims?.sub;
  }

  static get userSessionId() {
    return this.userClaims?.sid;
  }

  static get userUserName() {
    return this.getShipStationUserClaim('username');
  }

  static get userName() {
    return this.userClaims?.name;
  }

  static get userGivenName() {
    return this.userClaims?.given_name;
  }

  static get userFamilyName() {
    return this.userClaims?.family_name;
  }

  static get userNickname() {
    return this.userClaims?.nickname;
  }

  static get userPicture() {
    return this.userClaims?.picture;
  }

  static get userEmail() {
    return this.userClaims?.email;
  }

  static get userEmailVerified() {
    return this.userClaims?.email_verified;
  }

  static get userSellerId() {
    return this.getShipStationUserClaim('seller_id');
  }

  static get userPartition() {
    return this.getShipStationUserClaim('partition');
  }

  static get userPartitionHost() {
    return this.getShipStationUserClaim('partition_host');
  }

  static get userHomeCountry() {
    return this.getShipStationUserClaim('seller_home_country');
  }

  static get userRoleSetMask() {
    return this.getShipStationUserClaim('role_set');
  }

  static get userClaims() {
    return this.userIdentityTokenDecoded?.claims;
  }

  static getShipStationUserClaim = (claim) => {
    const claimSet = this.userClaims;
    if (!claimSet) {
      return null;
    }
    return claimSet[`${Auth0Constants.AUTH0_CLAIM_NAMESPACE}${claim}`];
  };

  // ACCESS TOKEN

  static get userAccessToken() {
    return this.userAccessTokenBody?.access_token;
  }

  static get userAccessTokenBody() {
    return this._userAccessTokenDecoded?.body;
  }

  static userAccessTokenBodyByAudience(audience) {
    return Auth0LocalStorageHelper.accessTokenEntryByAudience(audience)?.entryValue?.body?.access_token;
  }

  static userAccessTokenBodyByClientAndAudience(clientId, audience) {
    return Auth0LocalStorageHelper.accessTokenEntryByClientAndAudience(clientId, audience)?.entryValue?.body?.access_token;
  }

  static get userAccessTokenExpiry() {
    return this._userAccessTokenDecoded?.expiresAt;
  }

  static get userAccessTokenExpiresIn() {
    return this.userAccessTokenBody?.expires_in;
  }

  static get userAccessTokenClientId() {
    return this.userAccessTokenBody?.client_id;
  }

  static get userAccessTokenScope() {
    return this.userAccessTokenBody?.scope;
  }

  static get userAccessTokenScopeArray() {
    return this.userAccessTokenScope?.split(' ') || [];
  }

  static get userAccessTokenOauthScope() {
    return this.userAccessTokenBody?.oauthTokenScope;
  }

  static get userAccessTokenOauthScopeArray() {
    return this.userAccessTokenOauthScope?.split(' ') || [];
  }

  static get _userAccessTokenDecoded() {
    return Auth0LocalStorageHelper.accessTokenEntry?.entryValue;
  }

  static get _userIdentityTokenValueObject() {
    return Auth0LocalStorageHelper.identityTokenEntry?.entryValue;
  }

  // REFRESH TOKEN

  static get userRefreshToken() {
    return this.userAccessTokenBody?.refresh_token;
  }

  static userRefreshTokenByAudience(audience) {
    return Auth0LocalStorageHelper.accessTokenEntryByAudience(audience)?.entryValue?.body?.refresh_token;
  }

  static userRefreshTokenByClientAndAudience(clientId, audience) {
    return Auth0LocalStorageHelper.accessTokenEntryByClientAndAudience(clientId, audience)?.entryValue?.body?.refresh_token;
  }

  // UTILITY

  static deleteAuth0IsAuthenticatedCookies() {
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IS_AUTHENTICATED);
    const currentAuth0ClientId = this.userAccessTokenClientId;
    if (currentAuth0ClientId) {
      Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IS_AUTHENTICATED_CLIENT(currentAuth0ClientId));
      Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IS_AUTHENTICATED_LEGACY_CLIENT(currentAuth0ClientId));
    }
  }

  /**
   * Clears base auth-related cookies used during login/logout flows.
   * Note: Linking-specific cookies (AUTH0_IDP_REDIRECT_LOGIN, AUTH0_IDP_CONTEXT_KEY, etc.)
   * are intentionally NOT cleared here - they are cleared by the linking page helper's
   * _preLogoutActions override, as they need to persist through certain logout flows.
   */
  static clearAuthCookies() {
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IS_LOGIN_PROVIDER);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_ACCESS_TOKEN_V3_KEY);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_ACCESS_TOKEN_V2_KEY);
    this.deleteAuth0IsAuthenticatedCookies();
  }

  // AUTH0 SPAJS SESSION STATE
  static get auth0SpaJsSessionStateObj() {
    const keys = Object.keys(sessionStorage);
    const sessionStateKey = keys.find(key => key.startsWith('a0.spajs.txs'));
    const found = sessionStateKey ? JSON.parse(sessionStorage.getItem(sessionStateKey)) : null;

    if (found) {
      return found;
    }
    // else check cookie
    const sessionStateCookie = Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_SPAJS_SESSION_STATE);
    if (sessionStateCookie) {
      return JSON.parse(sessionStateCookie);
    }

    return null;
  }

  static get auth0SpaJsSessionRedirectUri() {
    return this.auth0SpaJsSessionStateObj?.redirect_uri;
  }

  static get auth0SpaJsSessionState() {
    return this.auth0SpaJsSessionStateObj?.state;
  }

  static get auth0SpaJsSessionAudience() {
    return this.auth0SpaJsSessionStateObj?.audience;
  }

  static get auth0SpaJsSessionNonce() {
    return this.auth0SpaJsSessionStateObj?.nonce;
  }

  static get auth0SpaJsSessionScope() {
    return this.auth0SpaJsSessionStateObj?.scope;
  }

  static get auth0SpaJsSessionAppState() {
    return this.auth0SpaJsSessionStateObj?.appState;
  }

  static copyAuth0SpaJsSessionStateToCookie(ttlMinutes = 10) {
    const sessionState = this.auth0SpaJsSessionStateObj;
    if (sessionState) {
      Auth0CookieHelper.setCookieValueWithExpirationInMinutes(Auth0Constants.AUTH0_SPAJS_SESSION_STATE, JSON.stringify(sessionState), ttlMinutes);
    }
  }
}

export default Auth0UserSessionHelper;
